import { PackageX, ArrowLeftRight, Ban, Shuffle } from 'lucide-react';
import type { TagTone } from '../../ui';

/**
 * THE SHORTFALL QUEUE'S VOCABULARY — shared by the register (ShortfallsQueuePage) and the record it
 * opens (ShortfallActionModal), so the two screens cannot drift into calling the same diagnosis two
 * different things or ageing the same row on two different scales.
 */

export interface ShortLine { productId: string; productName: string | null; shortQty: number }

export interface ShortfallRow {
  id: string;
  orderId: string;
  orderNo: string;
  /** The warehouse the shortfall was RAISED at — a snapshot, frozen on the row. */
  warehouseId: string;
  warehouseName: string | null;
  warehouseCode: string | null;
  /** The order's CURRENT dispatch warehouse. On an OPEN row it equals warehouseId (the queue retires
   *  a row whose warehouse moved); on a resolved one it is where the order actually went. */
  currentWarehouseId?: string | null;
  status: 'OPEN' | 'RESOLVED';
  outcome: string | null;
  reportedVia: string;
  shortSnapshot: ShortLine[] | string | null;
  pendingTotal: string;
  reportedById: string | null;
  reportedByName: string | null;
  reportedAt: string | null;
  remarks: string | null;
  resolution: string | null;
  resolvedAt: string | null;
  orderStatus: string;
  retailerName: string | null;
}

/* Each outcome is a DIFFERENT job for the planner — move stock, buy stock, or re-route the order —
   so each gets its own hue and its own icon rather than three shades of "problem". */
export const OUTCOME_LABEL: Record<string, { text: string; color: string; Icon: typeof PackageX }> = {
  NEEDS_TRANSFER: { text: 'Needs transfer', color: 'gold', Icon: ArrowLeftRight },
  INSUFFICIENT_TOTAL: { text: 'No stock anywhere', color: 'red', Icon: Ban },
  FULLY_COVERED_SINGLE: { text: 'Reassign warehouse', color: 'blue', Icon: Shuffle },
};

/* The diagnosis says WHAT is wrong; this says what to do about it. The queue's own description
   promises three moves — consolidate, restock, reassign — and until now the reader had to work out
   which of the three this row meant from a chip. */
export const NEXT_STEP: Record<string, string> = {
  NEEDS_TRANSFER: 'Pull the short units in from the site that has them.',
  FULLY_COVERED_SINGLE: 'One other site can pack it all — switch the warehouse.',
  INSUFFICIENT_TOTAL: 'No site can cover it — production or purchase.',
};

export const VIA_LABEL: Record<string, string> = {
  MANUAL: 'Reported', AUTO_PACK: 'Partial pack', AUTO_DISPATCH: 'Partial dispatch', AUTO: 'Detected',
};

/** Why a resolved row closed. `resolution` is the only field the queue read carries for it. */
export const RESOLUTION_LABEL: Record<string, string> = {
  AUTO: 'The order stopped being short — stock arrived, the warehouse moved, or it was packed.',
  AUTO_CANCELLED: 'The order was cancelled or closed, so the shortfall can never be fulfilled.',
};

/**
 * How long a shortfall has been sitting open.
 *
 * A shortfall is a blocked order: what ranks the planner's work is not that it exists but how long
 * it has been blocking. Same three coarse bands the approval queue uses — same day, a couple of
 * days, then red — so "old" means the same thing on both screens.
 */
export const daysSince = (d?: string | null) =>
  (d ? Math.max(0, Math.floor((Date.now() - new Date(d).getTime()) / 86_400_000)) : null);

export const AGE_TONE = (n: number): TagTone => (n >= 3 ? 'red' : n >= 1 ? 'gold' : 'green');

/**
 * How long a shortfall ACTUALLY blocked — raised until resolved.
 *
 * `daysSince` answers a different question ("how long has this been open"), and on a closed row it
 * keeps counting for ever: a shortfall raised yesterday and cleared the same hour would go on
 * reporting a bigger and bigger number, under a label that says how long it was open. Two numbers,
 * one label. Falls back to the still-running clock while a row has no resolved stamp.
 */
export const daysOpen = (from?: string | null, to?: string | null) => {
  if (!from) return null;
  const a = new Date(from).getTime();
  const b = to ? new Date(to).getTime() : Date.now();
  if (!Number.isFinite(a) || !Number.isFinite(b)) return null;
  return Math.max(0, Math.floor((b - a) / 86_400_000));
};

/** The short lines, whether the API handed them back as JSON or as a JSON string. */
export function parseSnapshot(s: ShortfallRow['shortSnapshot']): ShortLine[] {
  if (!s) return [];
  if (typeof s === 'string') { try { return JSON.parse(s) as ShortLine[]; } catch { return []; } }
  return Array.isArray(s) ? s : [];
}
